import { Link } from "react-router-dom";
import { Landmark, Car, IndianRupee, FileText, ArrowRight, Percent } from "lucide-react";
import Reveal from "../Plan2Benefit/Reveal.jsx";
import SectionHeading from "../Plan2Benefit/SectionHeading.jsx";
import Magnetic from "./Magnetic.jsx";
import { carLoanBenefits } from "./data.js";
import personalVehiclesPhoto from "../../assets/car_loan_personal_vehicle.png";
import commercialVehiclesPhoto from "../../assets/car_loan_commercial_vehicle.png";
import applyForLoanCard from "../../assets/ic_apply_for_loan26.png";

/* ─────────────────────────────────────────────────────────────
   CarLoanPromo — replaces the earlier LiveDemo (iPhone-frame
   dealer-quote) section in this slot. Same "First Month EMI
   Free" promise, presented as a single lead-gen banner rather
   than an embedded interactive demo, ending in one CTA that
   takes the user to the real application page (CarLoanApply.jsx)
   instead of the app store.

   Two-column layout: pitch + benefits on the left, a stacked
   pair of vehicle-category photo cards (personal / commercial)
   on the right — gives the section a visual anchor instead of
   floating on plain background.

   LiveDemo.jsx and its standalone demo page are left in place,
   just no longer rendered here — nothing else links to this slot
   by name, so removing it from Plan2BenefitCarVersion.jsx is a
   safe, reversible swap.
───────────────────────────────────────────────────────────── */
const BENEFIT_ICONS = {
  bank: Landmark,
  car: Car,
  "loan-amount": IndianRupee,
  "emi-amount": FileText,
};

export default function CarLoanPromo() {
  return (
    <section className="relative overflow-hidden border-y border-[#eceafb] bg-gradient-to-b from-[#faf9ff] via-white to-[#faf9ff] px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div
        className="pointer-events-none absolute -left-12 -top-16 -z-10 h-[380px] w-[380px] rounded-full bg-[#4328E7]/18 blur-[85px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-16 -bottom-20 -z-10 h-[420px] w-[420px] rounded-full bg-[#4328E7]/14 blur-[95px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-10 top-10 -z-10 h-[220px] w-[220px] rounded-full bg-[#24BF9B]/16 blur-[70px]"
        aria-hidden="true"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-20 top-1/3 -z-10 hidden h-[360px] w-[360px] rounded-full ring-2 ring-[#4328E7]/15 lg:block"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-4 -z-10 hidden h-[260px] w-[260px] rounded-full ring-2 ring-[#24BF9B]/25 lg:block"
      />

      <Reveal className="relative mx-auto max-w-7xl">
        {/* Mobile only: the whole pitch collapses to one tappable card —
            same "First Month EMI Free" artwork, straight through to the
            application form. Desktop (lg+) is untouched below. */}
        <Link
          to="/car-loan/apply"
          className="block overflow-hidden rounded-[1.75rem] shadow-[0_30px_60px_-28px_rgba(13,8,40,0.4)] transition duration-300 active:scale-[0.98] lg:hidden"
        >
          <img
            src={applyForLoanCard}
            alt="First Month EMI Free — apply for a car loan in under 2 minutes"
            className="w-full object-cover"
          />
        </Link>

        <div className="hidden items-center gap-14 lg:grid lg:grid-cols-2 lg:gap-16">
          {/* ── Left: pitch, benefits, CTA ───────────────────── */}
          <div className="relative flex flex-col items-start text-left">
            <div className="relative">
              <SectionHeading
                eyebrow="Apply in under 2 minutes"
                title="First Month"
                highlight="EMI Free*"
              />
              <svg
                aria-hidden="true"
                viewBox="0 0 40 40"
                className="pointer-events-none absolute -right-8 top-1 hidden h-9 w-9 text-[#4328E7]/50 sm:block"
                fill="none"
              >
                <path d="M6 6l7 7M4 18l9 2M14 4l2 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>

            <span className="mt-6 inline-flex items-center gap-3 rounded-full bg-white py-2.5 pl-2.5 pr-6 font-montserrat text-[15px] font-bold text-[#0d0828] shadow-[0_16px_32px_-18px_rgba(67,40,231,0.5)] ring-1 ring-[#4328E7]/10">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gradient-to-br from-[#4328E7] to-[#6d63ff] text-white shadow-[0_8px_18px_-6px_rgba(67,40,231,0.6)] ring-4 ring-[#4328E7]/10">
                <Percent className="h-5 w-5" strokeWidth={2.5} />
              </span>
              lowest interest rate on
            </span>

            <div className="mt-7 grid w-full grid-cols-1 gap-3.5 sm:grid-cols-2">
              {carLoanBenefits.map((benefit) => {
                const Icon = BENEFIT_ICONS[benefit.id];
                return (
                  <div
                    key={benefit.id}
                    className="group flex items-center gap-3.5 rounded-2xl bg-[#f5f3ff] px-4 py-3.5 transition duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_20px_40px_-24px_rgba(67,40,231,0.4)]"
                  >
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-gradient-to-br from-[#4328E7] to-[#6d63ff] text-white shadow-[0_10px_20px_-8px_rgba(67,40,231,0.55)] transition duration-300 group-hover:scale-110">
                      {Icon && <Icon className="h-5 w-5" strokeWidth={2.25} />}
                    </span>
                    <span className="font-montserrat text-[14px] font-bold leading-tight text-[#0d0828]">
                      {benefit.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <Magnetic className="mt-10">
              <Link
                to="/car-loan/apply"
                className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-br from-[#4328E7] to-[#5b4bf5] px-9 py-4 font-montserrat text-[15px] font-bold text-white shadow-[0_26px_54px_-18px_rgba(67,40,231,0.6)] transition duration-300 hover:shadow-[0_30px_60px_-16px_rgba(67,40,231,0.7)] active:scale-[0.98]"
              >
                Apply Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Magnetic>

            <p className="mt-4 font-montserrat text-[12.5px] font-semibold text-[#0d0828]/45">
              No cost to start · Your money stays in your control
            </p>
          </div>

          {/* ── Right: personal / commercial vehicle photos ──── */}
          <div className="flex flex-col gap-5">
            <div className="overflow-hidden rounded-[1.75rem] shadow-[0_36px_70px_-30px_rgba(13,8,40,0.35)] transition duration-500 hover:-translate-y-1">
              <img
                src={personalVehiclesPhoto}
                alt="Personal Vehicles — row of personal vehicles in a modern showroom"
                className="aspect-[1774/887] w-full object-cover"
              />
            </div>

            <div className="overflow-hidden rounded-[1.75rem] shadow-[0_36px_70px_-30px_rgba(13,8,40,0.35)] transition duration-500 hover:-translate-y-1">
              <img
                src={commercialVehiclesPhoto}
                alt="Commercial Vehicles — row of commercial trucks parked outdoors"
                className="aspect-[1672/941] w-full object-cover"
              />
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
